/**
 * useTelemetry Hook
 *
 * Returns exception tracking from the Shell framework's context-provided
 * telemetryClient. Use this in React components for error reporting.
 *
 * @module telemetry
 */
import { useCallback } from 'react';
import { useAppContext } from '@/Environment/useContext';
import { SeverityLevel } from '@microsoft/applicationinsights-common';
import type { TelemetryClient } from '@micro-frontend-react/employee-experience/lib/TelemetryClient';
import { EventType } from '@micro-frontend-react/employee-experience/lib/UsageTelemetry';
import { withCorrelation } from './utils';

/** Returns telemetry tracking functions for use in React components. */
export function useTelemetry() {
    const { telemetryClient } = useAppContext();

    const trackException = useCallback(
        (error: Error, properties?: Record<string, string>) =>
            telemetryClient.trackException({ exception: error }, withCorrelation(properties)),
        [telemetryClient],
    );

    const trackTrace = useCallback(
        (
            message: string,
            severityLevel: SeverityLevel = SeverityLevel.Information,
            properties?: Record<string, string>,
        ) => {
            telemetryClient.trackTrace(
                { message, severityLevel },
                withCorrelation(properties),
            );
        },
        [telemetryClient],
    );

    const trackPageView = useCallback(
        (name: string, properties?: Record<string, string>) => {
            if (typeof (telemetryClient as unknown as Record<string, unknown>).trackPageView !== 'function') {
                return;
            }

            (telemetryClient as unknown as TelemetryClient).trackPageView({
                name,
                properties: withCorrelation(properties),
                type: EventType.User,
                eventName: name,
            });
        },
        [telemetryClient],
    );

    return { trackException, trackTrace, trackPageView } as const;
}
